import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { UploadedFileModel, FilesState } from '@shared';
import { UploadToS3UseCase } from '@features/files';
import { InvestigationFilesRepository } from '../../../infrastructure/repositories/investigation-files.repository';
import { UploadInvestigationFilesCommand } from './upload-investigation-files.command';
import { UploadInvestigationFilesResult } from './upload-investigation-files.result';

/**
 * Orchestrator use case for uploading files for an investigation.
 * Handles the complete 3-step upload flow:
 * 1. Get presigned URLs from backend
 * 2. Upload files to S3 with progress tracking
 * 3. Confirm uploads with backend to create investigation attachments
 *
 * Processes multiple files in parallel and returns partial results
 * if some uploads fail.
 */
@Injectable({ providedIn: 'root' })
export class UploadInvestigationFilesUseCase {
  private readonly _repository = inject(InvestigationFilesRepository);
  private readonly _uploadToS3UseCase = inject(UploadToS3UseCase);
  private readonly _state = inject(FilesState);

  /**
   * Executes the complete upload flow for multiple files.
   *
   * @param command - Command containing investigation ID and files to upload
   * @returns Observable of upload result with uploaded files and errors
   */
  public execute(command: UploadInvestigationFilesCommand): Observable<UploadInvestigationFilesResult> {
    if (command.files.length === 0) {
      return of({ uploadedFiles: [], errors: [] });
    }

    // Process all files in parallel
    const uploadObservables = command.files.map((file) =>
      this._uploadSingleFile(command.investigationId, file).pipe(
        catchError((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          return of({
            success: false,
            fileName: file.name,
            error: errorMessage,
          } as UploadResult);
        })
      )
    );

    return forkJoin(uploadObservables).pipe(
      map((results) => {
        const uploadedFiles: UploadedFileModel[] = [];
        const errors: { fileName: string; error: string }[] = [];

        results.forEach((result) => {
          if (result.success && result.uploadedFile) {
            uploadedFiles.push(result.uploadedFile);
          } else if (!result.success && result.error) {
            errors.push({ fileName: result.fileName, error: result.error });
          }
        });

        return { uploadedFiles, errors };
      })
    );
  }

  /**
   * Handles the complete upload flow for a single file.
   */
  private _uploadSingleFile(investigationId: string, file: File): Observable<UploadResult> {
    return this._repository.getPresignedUploadUrl(investigationId, file).pipe(
      switchMap((presignedUpload) =>
        this._uploadToS3UseCase
          .execute({
            presignedUrl: presignedUpload.uploadUrl,
            file,
            fileId: presignedUpload.fileId,
            requiredHeaders: presignedUpload.requiredHeaders,
          })
          .pipe(
            switchMap(() =>
              this._repository.confirmUpload(investigationId, presignedUpload.fileId, file, presignedUpload.s3Key).pipe(
                map((uploadedFile) => {
                  this._state.completeUpload(presignedUpload.fileId, uploadedFile);
                  return uploadedFile;
                })
              )
            ),
            map((uploadedFile) => ({
              success: true,
              fileName: file.name,
              uploadedFile,
            })),
            catchError((error: unknown) => {
              const errorMessage = error instanceof Error ? error.message : 'Upload failed';
              this._state.failUpload(presignedUpload.fileId, errorMessage);
              throw error;
            })
          )
      )
    );
  }
}

/**
 * Internal result type for tracking individual file uploads.
 */
interface UploadResult {
  success: boolean;
  fileName: string;
  uploadedFile?: UploadedFileModel;
  error?: string;
}
